import React, { useState, useEffect, useRef } from 'react';
import { CONTACT_EMAIL } from '../site';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong while sending your message.';

const EMPTY_FORM = {
  name: '',
  companyName: '',
  email: '',
  message: '',
};

type FormState = typeof EMPTY_FORM;
type FieldName = 'name' | 'email' | 'message';

// The modal unmounts this form when it closes, so the draft is kept outside React:
// a stray backdrop click must not cost the visitor a half-written message. Keyed by
// idPrefix, so the modal's draft also survives a walk over to /contact.
const drafts = new Map<string, FormState>();

interface ContactFormProps {
  idPrefix: string;
  subject?: string;
  onSent?: () => void;
  onBusyChange?: (busy: boolean) => void;
  successAction?: React.ReactNode;
}

const ContactForm: React.FC<ContactFormProps> = ({ idPrefix, subject, onSent, onBusyChange, successAction }) => {
  const [formData, setFormData] = useState<FormState>(() => drafts.get(idPrefix) ?? EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<FieldName, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(DEFAULT_ERROR_MESSAGE);
  const successRef = useRef<HTMLDivElement>(null);

  const fieldId = (field: keyof FormState) => `${idPrefix}-${field}`;

  useEffect(() => {
    if (!subject) return;
    setFormData(prev => (prev.message.trim() ? prev : { ...prev, message: `I'm interested in ${subject}.` }));
  }, [subject]);

  useEffect(() => {
    drafts.set(idPrefix, formData);
  }, [idPrefix, formData]);

  // The modal's close button locks while a send is in flight; dismissing the dialog
  // mid-request would unmount the only thing that can report the outcome.
  useEffect(() => {
    onBusyChange?.(isSubmitting);
  }, [onBusyChange, isSubmitting]);

  // Submitting replaces the form with the confirmation, so the element focus was on is
  // gone and focus would fall to <body>: a keyboard user loses their place and a screen
  // reader announces nothing at the one moment the visitor has actually given something.
  useEffect(() => {
    if (isSubmitted) successRef.current?.focus();
  }, [isSubmitted]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value,
    }));
    setFieldErrors(prevErrors => {
      if (!(name in prevErrors)) return prevErrors;
      const nextErrors = { ...prevErrors };
      delete nextErrors[name as FieldName];
      return nextErrors;
    });
  };

  const validate = (): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Add your name so we know who we are replying to.';
    }
    if (!formData.email.trim()) {
      errors.email = 'Add an email address so we can reply.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email.trim())) {
      errors.email = 'That address looks incomplete. Check for a typo.';
    }
    if (!formData.message.trim()) {
      errors.message = 'Tell us briefly what you need help with.';
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIsError(false);
      setIsSubmitted(false);
      const firstInvalid = (['name', 'email', 'message'] as FieldName[]).find(field => errors[field]);
      if (firstInvalid) {
        document.getElementById(fieldId(firstInvalid))?.focus();
      }
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);
    setIsError(false);
    setErrorMessage(DEFAULT_ERROR_MESSAGE);
    setIsSubmitted(false);

    const apiUrl = process.env.REACT_APP_API_URL;
    if (!apiUrl) {
      console.error("API URL is not defined. Make sure REACT_APP_API_URL is set.");
      setErrorMessage("We couldn't reach our contact service.");
      setIsError(true);
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch(`${apiUrl}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const responseData = await response.json();

      if (response.ok) {
        setIsSubmitted(true);
        setFormData(EMPTY_FORM);
        drafts.delete(idPrefix);
        onSent?.();
      } else {
        setErrorMessage(responseData.message || 'We could not send your message.');
        setIsError(true);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      setErrorMessage('We could not reach the server. Your message has not been sent.');
      setIsError(true);
    }
    setIsSubmitting(false);
  };

  const fieldClassName = (field: keyof FormState) =>
    `mt-1 block w-full px-3 py-2 bg-gray-700 border rounded-md shadow-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:border-accent-400 sm:text-sm text-white disabled:opacity-50 ${
      field !== 'companyName' && fieldErrors[field as FieldName] ? 'border-red-400' : 'border-gray-600'
    }`;

  const describedBy = (field: FieldName) => (fieldErrors[field] ? `${fieldId(field)}-error` : undefined);

  if (isSubmitted) {
    return (
      <div
        ref={successRef}
        role="status"
        tabIndex={-1}
        className="border-t-2 border-accent-500 py-6 focus:outline-none"
      >
        <h3 className="font-display text-display-sm font-medium text-white">Message sent.</h3>
        <p className="mt-3 max-w-measure text-gray-300">
          It is with us now. We read every message and reply ourselves &mdash; no autoresponder,
          no sequence.
        </p>
        <p className="mt-3 max-w-measure text-gray-400">
          If a call would be faster, the assessment is thirty minutes and free.
        </p>
        {successAction && <div className="mt-6">{successAction}</div>}
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-6">
      <div>
        <label htmlFor={fieldId('name')} className="block text-sm font-medium text-gray-300">
          Name <span className="text-red-400">*</span>
        </label>
        <input
          type="text"
          name="name"
          id={fieldId('name')}
          autoComplete="name"
          maxLength={100}
          value={formData.name}
          onChange={handleChange}
          required
          aria-required="true"
          aria-invalid={!!fieldErrors.name}
          aria-describedby={describedBy('name')}
          disabled={isSubmitting}
          className={fieldClassName('name')}
        />
        {fieldErrors.name && (
          <p id={`${fieldId('name')}-error`} role="alert" className="mt-1 text-sm text-red-300">
            {fieldErrors.name}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={fieldId('companyName')} className="block text-sm font-medium text-gray-300">
          Company name
        </label>
        <input
          type="text"
          name="companyName"
          id={fieldId('companyName')}
          autoComplete="organization"
          maxLength={100}
          value={formData.companyName}
          onChange={handleChange}
          disabled={isSubmitting}
          className={fieldClassName('companyName')}
        />
      </div>

      <div>
        <label htmlFor={fieldId('email')} className="block text-sm font-medium text-gray-300">
          Email <span className="text-red-400">*</span>
        </label>
        <input
          type="email"
          name="email"
          id={fieldId('email')}
          autoComplete="email"
          maxLength={254}
          value={formData.email}
          onChange={handleChange}
          required
          aria-required="true"
          aria-invalid={!!fieldErrors.email}
          aria-describedby={describedBy('email')}
          disabled={isSubmitting}
          className={fieldClassName('email')}
        />
        {fieldErrors.email && (
          <p id={`${fieldId('email')}-error`} role="alert" className="mt-1 text-sm text-red-300">
            {fieldErrors.email}
          </p>
        )}
      </div>

      <div>
        <label htmlFor={fieldId('message')} className="block text-sm font-medium text-gray-300">
          Message <span className="text-red-400">*</span>
        </label>
        <textarea
          name="message"
          id={fieldId('message')}
          rows={4}
          maxLength={4000}
          value={formData.message}
          onChange={handleChange}
          required
          aria-required="true"
          aria-invalid={!!fieldErrors.message}
          aria-describedby={describedBy('message')}
          disabled={isSubmitting}
          className={fieldClassName('message')}
        />
        {fieldErrors.message && (
          <p id={`${fieldId('message')}-error`} role="alert" className="mt-1 text-sm text-red-300">
            {fieldErrors.message}
          </p>
        )}
      </div>

      {isError && (
        <div role="alert" className="rounded-md border border-red-400/40 bg-red-400/10 p-3">
          <p className="text-sm text-red-300">{errorMessage}</p>
          <p className="mt-1 text-sm text-gray-300">
            Email us at{' '}
            <a
              href={`mailto:${CONTACT_EMAIL}`}
              className="rounded underline hover:text-white focus:outline-none focus:ring-2 focus:ring-accent-400"
            >
              {CONTACT_EMAIL}
            </a>{' '}
            and we will pick it up from there.
          </p>
        </div>
      )}

      <div>
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex min-h-[44px] w-full items-center justify-center rounded-md border border-transparent bg-accent-500 px-4 py-2 text-sm font-medium text-gray-950 shadow-sm transition-colors duration-150 hover:bg-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-gray-900 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {isSubmitting ? 'Sending…' : 'Send message'}
        </button>
      </div>
    </form>
  );
};

export default ContactForm;
